import {
  DateTimeRange,
  EventAddress,
  SanityBlockContent,
  SanityEventPreview,
} from '@dap/sanity/types';
import { UserData } from '@dap/memberadmin/types';
import { format, isSameDay, parseISO } from 'date-fns';
import { SanityDapUserRole, SanityUserRole } from '@dap/common/types';

export const sanityBrandNamePlaceholder = '{{brand}}';

export const replacePlaceholderWithBrandName = (text: string, brandName: string) =>
  text.split(sanityBrandNamePlaceholder).join(brandName);

export const replaceBodyBrandNamePlaceholder = (
  sanityBody: SanityBlockContent[],
  brandName: string
): SanityBlockContent[] => {
  return sanityBody.map((bodyPiece) => {
    if (bodyPiece.children) {
      const formattedChildren = bodyPiece.children.map((bodyChild: any) => {
        const text = bodyChild.text
          ? replacePlaceholderWithBrandName(bodyChild.text, brandName)
          : undefined;
        return { ...bodyChild, text };
      });
      return { ...bodyPiece, children: formattedChildren };
    }
    return bodyPiece;
  });
};

export type GroupedEvent = {
  [key: string]: SanityEventPreview[];
};

export const groupEventsByMonthYear = (events: SanityEventPreview[]) => {
  return events.reduce((groupedEvents, event) => {
    const monthYear = format(new Date(event.eventInformation.dateTimeRange.startDate), 'yyyy-MM');
    if (groupedEvents[monthYear]) {
      groupedEvents[monthYear].push(event);
    } else {
      groupedEvents[monthYear] = [event];
    }
    return groupedEvents;
  }, {} as GroupedEvent);
};

export function getHrefValueForLink(link: string) {
  if (link.startsWith('https://') || link.startsWith('http://') || link.startsWith('mailto:')) {
    return link;
  }
  return `https://${link}`;
}

export function getFullAddress(address: EventAddress) {
  const postalCodeAndCity =
    address.postalCode && address.city ? `${address.postalCode} ${address.city}` : null;
  const fullAddress = [address.street, postalCodeAndCity, address.addressFreeText]
    .filter(Boolean)
    .join(', ');
  return fullAddress;
}

export function getDateRange(dateTimeRange: DateTimeRange) {
  const startDate = parseISO(dateTimeRange.startDate);
  const endDate = dateTimeRange.endDate ? parseISO(dateTimeRange.endDate) : null;
  const formattedStartDate = format(startDate, 'PPp');

  if (!endDate) {
    return formattedStartDate;
  } else {
    if (isSameDay(startDate, endDate)) {
      return `${formattedStartDate} – ${format(endDate, 'p')}`;
    } else {
      return `${formattedStartDate} – ${format(endDate, 'PPp')}`;
    }
  }
}

// Returns true if the user has the specified role in any org unit
export function hasSanityRole(user: UserData, role: SanityUserRole): boolean {
  return user.roles.some((r) => r.role.key === role);
}

// Returns true if the user has any of the specified roles in any org unit
export function hasSomeSanityRoles(user: UserData, roles: SanityUserRole[]): boolean {
  return user.roles.some((r) => roles.includes(r.role.key as SanityUserRole));
}

// Returns true if the user is a DAP brand editor for the specified brand
export function isSanityDapBrandEditor(user: UserData, brandKey: string): boolean {
  return user.roles.some(
    (r) => r.role.key === SanityDapUserRole.BrandEditor && r.orgUnitKey === brandKey
  );
}

// Returns true if the user is a DAP brand administrator for the specified brand
export function isSanityDapBrandAdministrator(user: UserData, brandKey: string): boolean {
  return user.roles.some(
    (r) => r.role.key === SanityDapUserRole.BrandAdministrator && r.orgUnitKey === brandKey
  );
}

// Returns true if the user has the DAP shop editor role in any org unit
export function isSanityDapShopEditor(user: UserData): boolean {
  return hasSanityRole(user, SanityDapUserRole.ShopEditor);
}

// Returns true if the user has the DAP subject editor role in any org unit
export function isSanityDapSubjectEditor(user: UserData): boolean {
  return hasSanityRole(user, SanityDapUserRole.SubjectEditor);
}

// Returns true if the user has the DAP administrator role in any org unit
export function isSanityDapAdministrator(user: UserData): boolean {
  return hasSanityRole(user, SanityDapUserRole.Administrator);
}

/**
 * Returns true if the user has access to the DAP sanity studio
 *
 * A user might have access if they have any of the following roles:
 * - BrandEditor
 * - BrandAdministrator
 * - ShopEditor
 * - Administrator
 */
export function hasSanityDapAccess(user: UserData): boolean {
  return hasSomeSanityRoles(user, [
    SanityDapUserRole.BrandEditor,
    SanityDapUserRole.BrandAdministrator,
    SanityDapUserRole.ShopEditor,
    SanityDapUserRole.SubjectEditor,
    SanityDapUserRole.Administrator,
  ]);
}

// Returns a mocked user object with additional roles to be used in development (for situations where new roles needs to be tested before they have been added correctly to kjedeadmin)
export function mockUserData(
  user: UserData,
  options?: {
    dapAdmin?: boolean;
    dapBrandEditor?: string;
    dapBrandAdministrator?: string;
    dapShopEditor?: boolean;
    dapSubjectEditor?: boolean;
  }
) {
  console.log('%c Mocking userData!', 'color: #f00; background-color: #ff0;', options, user);

  const mockedUser: UserData = {
    ...user,
    roles: [
      ...(options?.dapAdmin
        ? [
            {
              orgUnitKey: 'blinkhus',
              type: 'brand',
              role: {
                key: SanityDapUserRole.Administrator,
                title: 'Sanityadministrator, DAP',
                selected: false,
                isAdmin: false,
              },
            },
          ]
        : []),
      ...(options?.dapBrandEditor
        ? [
            {
              orgUnitKey: options.dapBrandEditor,
              type: 'brand',
              role: {
                key: SanityDapUserRole.BrandEditor,
                title: 'Sanityredaktør, DAP',
                selected: false,
                isAdmin: false,
              },
            },
          ]
        : []),
      ...(options?.dapBrandAdministrator
        ? [
            {
              orgUnitKey: options.dapBrandAdministrator,
              type: 'brand',
              role: {
                key: SanityDapUserRole.BrandAdministrator,
                title: 'Sanityadministrator, DAP',
                selected: false,
                isAdmin: false,
              },
            },
          ]
        : []),
      ...(options?.dapShopEditor
        ? [
            {
              orgUnitKey: 'blinkhus',
              type: 'brand',
              role: {
                key: SanityDapUserRole.ShopEditor,
                title: 'Sanityredaktør, DAP',
                selected: false,
                isAdmin: false,
              },
            },
          ]
        : []),
      ...(options?.dapSubjectEditor
        ? [
            {
              orgUnitKey: 'blinkhus',
              type: 'brand',
              role: {
                key: SanityDapUserRole.SubjectEditor,
                title: 'Sanityredaktør, DAP',
                selected: false,
                isAdmin: false,
              },
            },
          ]
        : []),
    ],
  };

  return mockedUser;
}
